import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, UserPlus } from 'lucide-react';
import { TeamMemberItem } from './TeamMemberItem';
import type { TeamMemberRole, TeamMembership } from '@arrowise/shared';

interface TeamMembersCardProps {
    members: TeamMembership[];
    canManage?: boolean;
    canModify?: boolean;
    currentUserId?: string;
    onInviteMember?: () => void;
    onRoleChange?: (memberId: string, newRole: TeamMemberRole, memberName: string) => void;
    onRemoveMember?: (memberId: string, memberName: string) => void;
    showInviteButton?: boolean;
    showJoinDate?: boolean;
    showRemoveButton?: boolean;
    title?: string;
    description?: string;
}

export const TeamMembersCard: React.FC<TeamMembersCardProps> = ({
    members,
    canManage = false,
    canModify = false,
    currentUserId,
    onInviteMember,
    onRoleChange,
    onRemoveMember,
    showInviteButton = false,
    showJoinDate = true,
    showRemoveButton = true,
    title,
    description,
}) => {
    const cardTitle = title || `Team Members (${members.length})`;
    const cardDescription = description || (canManage
        ? 'Manage your team members and their roles'
        : 'View team members and their roles');

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <User className="size-5" />
                            {cardTitle}
                        </CardTitle>
                        <CardDescription>
                            {cardDescription}
                        </CardDescription>
                    </div>
                    {showInviteButton && onInviteMember && (
                        <Button onClick={onInviteMember}>
                            <UserPlus />
                            Invite Member
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {members.map((member) => (
                        <TeamMemberItem
                            key={member.id}
                            member={member}
                            canModify={
                                canModify && member.role !== 'owner' && member.userId !== currentUserId
                            }
                            onRoleChange={onRoleChange}
                            onRemove={onRemoveMember}
                            showJoinDate={showJoinDate}
                            showRemoveButton={showRemoveButton}
                        />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};
